import React from "react";
import { MapPin, Calendar, Plane, Music, Users, Tent, Bus, Car, Flame } from "lucide-react";
import { tr } from "../lib/i18n";

const ICONS = {
  letenky: Plane,
  zajezd: Bus,
  vylet: Car,
  koncert: Music,
  festival: Tent,
  sraz: Users,
};

function formatPrice(czk, lang) {
  if (!czk || czk === 0) return tr(lang, "card.free");
  return new Intl.NumberFormat(lang === "en" ? "en-GB" : "cs-CZ", {
    style: "currency",
    currency: "CZK",
    maximumFractionDigits: 0,
  }).format(czk);
}

export default function TicketCard({ event, lang, onOpen, index = 0 }) {
  const Icon = ICONS[event.category] || Users;
  const seatsLeft = Math.max(0, (event.capacity || 0) - (event.attendees || 0));
  const soldOut = event.capacity > 0 && seatsLeft === 0;
  const lastSpot = event.capacity > 0 && seatsLeft === 1;

  const ratioText = event.capacity > 0
    ? `${event.attendees}/${event.capacity} ${tr(lang, "card.attendees")}`
    : null;
  const ratioCls = soldOut || lastSpot ? "text-red-600" : "text-emerald-600";

  return (
    <article
      data-testid={`event-card-${event.id}`}
      onClick={() => onOpen(event)}
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
      className="animate-fade-up bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-[0_24px_48px_-20px_rgba(14,165,233,0.25)] hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col group overflow-hidden"
    >
      {/* PHOTO */}
      {event.photo_url && (
        <div className="relative w-full aspect-[16/10] bg-slate-100 overflow-hidden">
          <img
            src={event.photo_url}
            alt={event.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          {event.is_hot && (
            <span
              data-testid={`event-card-hot-${event.id}`}
              className="absolute top-3 left-3 inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100/95 backdrop-blur text-amber-700 shadow-sm"
            >
              <Flame className="w-3 h-3" /> HOT
            </span>
          )}
          {lastSpot && !soldOut && (
            <span className="absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full bg-red-500 text-white shadow-sm animate-pulse">
              Poslední místo!
            </span>
          )}
        </div>
      )}

      {/* TOP META */}
      <div className="p-5 sm:p-6 flex flex-col gap-3 flex-1">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-600 grid place-items-center">
            <Icon className="w-4 h-4" />
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">
            {tr(lang, `categories.${event.category}`)}
          </span>
        </div>

        <h3 className="font-heading font-bold text-lg sm:text-xl leading-tight text-slate-900">
          {event.title}
        </h3>

        <div className="flex flex-col gap-1.5 text-sm font-medium text-slate-600">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-400" />
            <span>{event.city}{event.country ? `, ${event.country}` : ""}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span>{event.date_text || event.date}</span>
          </div>
        </div>
      </div>

      {/* DASHED DIVIDER */}
      <div className="mx-5 sm:mx-6 ticket-divider" />

      {/* BOTTOM */}
      <div className="p-5 sm:p-6 flex items-end justify-between gap-3">
        <div className="flex flex-col">
          <span className="text-xs uppercase tracking-[0.12em] font-bold text-slate-400">
            {event.perks || event.price_label || (event.price_czk ? "Cena" : tr(lang, "card.free"))}
          </span>
          <span className="font-heading font-extrabold text-xl text-slate-900 leading-none mt-1">
            {formatPrice(event.price_czk, lang)}
          </span>
          {ratioText && (
            <span data-testid={`event-card-seats-${event.id}`} className={`text-xs font-bold mt-1.5 ${ratioCls}`}>
              {soldOut ? tr(lang, "card.sold_out") : ratioText}
            </span>
          )}
        </div>
        <button
          data-testid={`event-card-join-${event.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpen(event);
          }}
          className="px-5 py-2.5 rounded-full bg-sky-50 text-sky-700 font-bold text-sm group-hover:bg-sky-500 group-hover:text-white transition-colors active:scale-95"
        >
          {tr(lang, "card.join")}
        </button>
      </div>
    </article>
  );
}
